import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
} from '@angular/core';
import {
  ApiParameters,
  DocumentsResult,
  DropDownFilterType,
  GridHeader,
  GridRowData,
  ProductsUserData,
  ProductType,
  ProductTypesData,
  Records,
  SubscriptionProductFilterData,
  SubscriptionProductFilterType,
  SubscriptionProductsData,
  SubscriptionProductSelectedFilters,
  SwitchState,
} from '@marketing/subscriptions-manage/subscription-manage.interface';
import { ProductTypeParameter } from '@products/services/graphql-fund-data.service';
import { BaseFilter } from '@products/fund-listing/services/fund-filter.service';
import {
  DropdownComponent,
  DropdownItem,
  DynamicTableColDef,
  FrkBreakpoint,
  ResponsiveService,
} from '@frk/eds-components';
import { Logger } from '@utils/logger';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';
import { SubscriptionsService } from '@marketing/subscriptions-manage/services/subscriptions.service';
import { SubscriptionProductsService } from '@marketing/subscriptions-manage/services/subscription-products.service';
import { ActivatedRoute } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';

/**
 * Logger
 */
const logger = Logger.getLogger('SubscriptionsProductsIiComponent');

@Component({
  selector: 'ft-subscriptions-products-iii',
  templateUrl: './subscriptions-products-iii.component.html',
  styleUrls: ['./subscriptions-products-iii.component.scss'],
})
export class SubscriptionsProductsIiiComponent implements OnInit, OnDestroy {
  @Input() apiUrl!: string;
  private productData: SubscriptionProductsData;
  private docTypesHeaders: GridHeader[];
  private productTypesData: ProductTypesData;
  private productsTypeData$: Observable<ProductTypesData>;
  private filterOptions$: Observable<SubscriptionProductFilterData>;
  private productsUserData$: Observable<ProductsUserData>;
  private unsubscribe$: Subject<void> = new Subject<void>();
  private userData: ProductsUserData;
  private fullData: GridRowData[];
  private filteredData: GridRowData[];
  public FrkBreakpoint = FrkBreakpoint;
  public isHandheld$: Observable<boolean>;
  public isCardView$: Observable<boolean>;
  public productTypeFilter: DropDownFilterType = {
    label: 'product-type',
    preSelectedValues: {
      value: ProductTypeParameter.MUTUAL_FUNDS,
      label: 'Mutual Funds',
    },
    type: new BaseFilter('PRODUCTS', () => true, []),
    options: [],
  };
  public otherFilterTypes: SubscriptionProductFilterType[];
  public isAnyFilterPresent: boolean;
  private selectedFilters: SubscriptionProductSelectedFilters[] = [];
  public colDefs!: DynamicTableColDef<GridRowData>[];
  public rowData!: GridRowData[];
  public records: Records = {
    showed: 0,
    all: 0,
  };
  public subscriptionOnlyState: SwitchState = SwitchState.OFF;
  public filterTypes: DropDownFilterType[];
  public textFilterValue = '';
  public resetFilterButton = false;
  public selectedItemsCount = 0;
  public hasSubscriptions = false;
  public isLoading = true;
  public isPopulated = false;
  public globalUnsubscribe = false;
  public loggedIn: boolean;
  public isDisabledSubject$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    true
  );
  @ViewChildren('filterDropdowns')
  filterDropdowns: QueryList<DropdownComponent>;

  constructor(
    private subscriptionsService: SubscriptionsService,
    private subscriptionsProductsService: SubscriptionProductsService,
    private route: ActivatedRoute,
    private spinner: NgxSpinnerService,
    private responsiveService: ResponsiveService,
    private changeDetector: ChangeDetectorRef
  ) {
    this.productTypesData = require('../__fixtures/subscribableProductTypeDocuments.json');
  }

  ngOnInit(): void {
    this.isHandheld$ = this.responsiveService.isHandheld$();
    this.isCardView$ = this.responsiveService.isBreakpointOrBelow$(
      FrkBreakpoint.Tablet
    );
    this.spinner
      .show('countLoadingSpinner')
      .then(() => logger.debug('Count Spinner set'));
    let globalId = null;
    this.productsUserData$ = this.route.queryParams.pipe(
      takeUntil(this.unsubscribe$),
      switchMap((params: { globalId: number }) => {
        globalId = params.globalId;
        if (!globalId) {
          // Set loggedIn to false when globalId is not set. This is required for MSD integration,
          // where globalID will be set after Okta verification.
          this.loggedIn = false;
          return;
        }
        this.loggedIn = true;
        return this.subscriptionsService.getApiGetRequest$(
          this.apiUrl + 'productSubscriptions/globalId/' + globalId
        );
      })
    );
    this.productsUserData$.pipe(takeUntil(this.unsubscribe$)).subscribe(
      (userData: ProductsUserData) => {
        this.subscriptionsProductsService.setUserData(userData);
        this.subscriptionsService.setContactId$({
          contactId: userData.contactId,
          globalId: userData.globalId,
          region: userData.region,
        });
        this.userData = this.subscriptionsProductsService.getUserData();
        this.globalUnsubscribe = this.userData.globalUnsubscribe;

        if (!this.globalUnsubscribe) {
          const apiParameters: ApiParameters = {};
          apiParameters.region = this.userData.region;
          apiParameters.investorType = this.userData.investorType;
          this.productsTypeData$ = this.subscriptionsService.getApiGetRequest$(
            this.apiUrl + 'subscribableProductTypeDocuments',
            apiParameters
          );
          this.productsTypeData$.pipe(takeUntil(this.unsubscribe$)).subscribe(
            (productsTypeData: ProductTypesData) => {
              if (productsTypeData.productTypes.length > 0) {
                this.productTypesData = productsTypeData;
              }
              this.getColDefs();
              this.setProductTypes(this.productTypesData);
              this.getTableData();
            },
            (error) => {
              logger.debug('Products data load error', error);
              this.rowData = null;
              this.setLoadingEnd();
            }
          );
          const params: ApiParameters = {};
          params.region = this.userData.region;
          this.filterOptions$ = this.subscriptionsService.getFilterOptionsRequest$(
            this.apiUrl + 'searchFilterOptions',
            params
          );
          this.filterOptions$
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((data: SubscriptionProductFilterData) => {
              const filterData = data?.searchFilterOptions;
              this.otherFilterTypes = this.subscriptionsProductsService.extractFilterData(
                filterData
              );
              this.isAnyFilterPresent = this.otherFilterTypes?.length > 0;
            });
        } else {
          this.setLoadingEnd();
        }
      },
      (error) => {
        logger.debug('User data load error', error);
        this.rowData = null;
        this.setLoadingEnd();
      }
    );
  }

  // ###############
  // PRIVATE METHODS
  // ###############
  // EDS dynamic-table setup
  /**
   * Get column definition for table
   */
  private getColDefs(
    docType: ProductTypeParameter | string = this.productTypesData
      .productTypes[0].typeId
  ): void {
    this.colDefs = [
      { field: 'ID', headerName: 'Subscribe' },
      {
        field: 'NAME',
        headerName: 'Fund Name',
        isCardTitle: true,
        dataType: 'string',
        sortable: true,
      },
      {
        field: 'DOCUMENTS',
        headerName: 'Subscribed Documents',
        dataType: 'string',
      },
      { field: '', headerName: '' },
    ];

    const selectedProductType: ProductType = this.productTypesData.productTypes.find(
      (node: ProductType) => {
        return node.typeId === docType;
      }
    );
    if (selectedProductType?.documentTypes) {
      this.subscriptionsProductsService.setDocumentTypes(
        selectedProductType.documentTypes
      );
      this.docTypesHeaders = this.subscriptionsProductsService.getDocumentTypes();
    }
  }

  /**
   * Setting data for EDS dynamic-table
   */
  private getTableData(
    productType: ProductTypeParameter | string = this.productTypesData
      .productTypes[0].typeId
  ): void {
    this.isLoading = true;
    const apiParameters: ApiParameters = {};
    apiParameters.region = this.userData.region;
    apiParameters.investorType = this.userData.investorType;
    if (productType !== '') {
      apiParameters.typeId = productType;
    }
    this.subscriptionsService
      .getApiGetRequest$(
        this.apiUrl + 'subscribableProductTypeProducts',
        apiParameters
      )
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (productData: SubscriptionProductsData) => {
          logger.debug(productData);
          this.productData = productData;
          this.subscriptionsProductsService.setRowData(productData);
          this.rowData = this.fullData = this.filteredData = this.subscriptionsProductsService.getRowData();
          this.records.all = this.records.showed = this.rowData.length;
          this.isPopulated = true;
          this.setLoadingEnd();
        },
        (error) => {
          logger.debug('Data loading error:', error);
          this.setLoadingEnd();
        }
      );
  }

  /**
   * Set Loading Data End
   */
  private setLoadingEnd = (): void => {
    this.isLoading = false;
    this.selectedItemsCount = 0;
    this.isDisabledSubject$.next(true);
    this.changeDetector.detectChanges();
  };

  // Filtering methods
  /**
   * Set Product Type Options
   * @param productTypesData - ProductTypesData
   */
  private setProductTypes(productTypesData: ProductTypesData): void {
    this.productTypeFilter.options = productTypesData.productTypes.map(
      (productType: ProductType) => {
        return { value: productType.typeId, label: productType.typeLabel };
      }
    );
    this.productTypeFilter.preSelectedValues = this.productTypeFilter.options[0];
  }

  /**
   * Set filter reset button status.
   */
  public setFilterReset(): void {
    if (this.fullData?.length !== this.rowData?.length) {
      this.resetFilterButton = true;
      return;
    }
    this.resetFilterButton = false;
  }

  /**
   * Filtering table data
   */
  private filterData(): void {
    this.rowData = this.subscriptionsProductsService.getRowData();
    this.filteredData = this.fullData.filter((row: GridRowData) => {
      return (
        row.NAME.toLowerCase().includes(this.textFilterValue.toLowerCase()) ||
        row.ID.includes(this.textFilterValue)
      );
    });
    if (this.subscriptionOnlyState === SwitchState.ON) {
      this.filteredData = this.filteredData.filter((row: GridRowData) => {
        row.isExpanded = false;
        return row.allActiveDocuments?.length > 0;
      });
    }
    this.rowData = this.filteredData;
    this.setFilterReset();
    this.records.showed = this.filteredData.length;
  }

  // ###############
  // PUBLIC METHODS
  // ###############
  public openPreferences($event: MouseEvent) {
    logger.debug('Expand Filters', $event);
    this.subscriptionsProductsService.setIsPreferencesModalVisible$(true);
  }

  /**
   * Resetting filters
   * @param $event - MouseEvent
   */
  public resetFilters($event: MouseEvent): void {
    logger.debug('Reset Filters', $event);
    this.textFilterValue = '';
    this.subscriptionOnlyState = SwitchState.OFF;
    this.filteredData = this.rowData = this.fullData;
    this.records.showed = this.rowData.length;
    this.resetFilterButton = false;
    this.selectedFilters = [];
    // Reset the 'dropdown' and 'search' components.
    this.filterDropdowns.forEach((dropdown) => dropdown.reset());
    this.subscriptionsProductsService.setSelectedFilters$(this.selectedFilters);
    $event.preventDefault();
  }

  /**
   * Update product type data in dynamic-table
   * @param $event - EDS DropdownItem
   */
  public updateProductTypeFilter($event: DropdownItem | DropdownItem[]) {
    this.subscriptionOnlyState = SwitchState.OFF;
    this.selectedFilters = [];
    // Reset the 'dropdown' and 'search' components.
    this.filterDropdowns.forEach((dropdown) => dropdown.reset());
    this.subscriptionsProductsService.setSelectedFilters$(this.selectedFilters);
    logger.debug('Update Product Type Filter', $event);
    const event = $event as DropdownItem;
    this.productTypeFilter.preSelectedValues = event;
    this.getTableData(event.value);
  }

  /**
   * Toggle filtering for products witch subscribed documents.
   * @param $event - string
   */
  public toggleSubscriptionsOnly($event: string) {
    logger.debug('Toggle Subscriptions Only filter', $event);
    this.subscriptionOnlyState = $event as SwitchState;
    this.filterData();
  }

  /**
   * Search By Keyword. Covers searching by product name and product OneTis (not case sensitive)
   * @param $event - string form entry.
   */
  public searchByKeyword($event: string) {
    logger.debug('Search By Keyword filter', $event);
    this.textFilterValue = $event;
    this.filterData();
  }

  public onDropdownChange(filterType: string, event: DropdownItem[]) {
    if (this.selectedFilters?.length) {
      const isFilter = this.selectedFilters.findIndex(
        (data) => data.filterType.toLowerCase() === filterType.toLowerCase()
      );
      if (isFilter > -1) {
        event?.length
          ? (this.selectedFilters[isFilter].values = event)
          : this.selectedFilters.splice(isFilter, 1);
      } else {
        this.selectedFilters.push({ filterType, values: event });
      }
    } else {
      this.selectedFilters.push({ filterType, values: event });
    }
    this.resetFilterButton = this.selectedFilters?.length > 0;
    this.subscriptionsProductsService.setSelectedFilters$(this.selectedFilters);
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.unsubscribe();
  }
}
